import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Center,
  Checkbox,
  Group,
  Loader,
  Modal,
  NumberInput,
  SegmentedControl,
  Stack,
  Text,
} from '@mantine/core';
import { IconAlertCircle, IconChevronRight, IconWaveSine } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import { PickStatusBadge } from './PickStatusBadge';
import type { PickStatus } from './api';
import {
  createWave,
  listWaveCandidates,
  listWaves,
  type PickWaveCandidateDto,
  type PickWaveCreatedDto,
  type PickWaveDto,
  type WaveMode,
} from './waveApi';

/**
 * Wave picks — M5.7.
 *
 * **A desk screen, on purpose.** legacy's wave screen is a desktop grid and so is this: deciding
 * which orders to combine is planning, done sitting down, and on a warehouse where stock will not
 * cover everything it decides whose order waits. The picker then walks the result through the
 * ordinary pick screens, because a wave IS a pick.
 *
 * **The skipped orders are the interesting half.** An order that did not make it into the wave is
 * shown with the reason, not quietly left out — "where did my order go" is the question this
 * screen exists to stop somebody asking. legacy prints exactly this as its ignored-order report.
 */
export function PickWavesRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const warehouseId = useCurrentWarehouseId();
  const [building, setBuilding] = useState(false);

  // Building a wave takes orders out of the queue and decides who is served first, so it is the
  // order desk's permission, not the picker's. The server asks for the same one.
  const canBuild = permissions.has('orders:write');

  const waves = useQuery({
    queryKey: ['pick-waves', warehouseId] as const,
    queryFn: ({ signal }) => listWaves(undefined, signal),
    enabled: Boolean(warehouseId),
  });

  const columns = useMemo<DataColumn<PickWaveDto>[]>(() => [
    {
      key: 'pickNumber',
      label: t('waves.number'),
      render: (w) => <Text fw={600}>{w.pickNumber}</Text>,
      card: 'title',
    },
    {
      key: 'status',
      label: t('waves.status'),
      render: (w) => <PickStatusBadge status={w.status as PickStatus} />,
      card: 'aside',
    },
    {
      key: 'orderCount',
      label: t('waves.orders'),
      align: 'right',
      render: (w) => w.orderCount,
    },
    {
      key: 'itemCount',
      label: t('waves.items'),
      align: 'right',
      render: (w) => w.itemCount,
    },
    {
      key: 'units',
      label: t('waves.units'),
      align: 'right',
      // Picked against requested, because a half-walked wave is the common state and the
      // difference is what tells somebody whether to go and find the picker.
      render: (w) => `${w.unitsPicked} / ${w.unitsRequested}`,
    },
  ], [t]);

  if (!warehouseId) {
    return (
      <Stack>
        <PageHeader title={t('waves.title')} />
        <Alert icon={<IconAlertCircle size={16} />} color="yellow">
          {t('waves.chooseWarehouse')}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack>
      <PageHeader
        title={t('waves.title')}
        subtitle={t('waves.subtitle')}
        actions={canBuild ? (
          <Button leftSection={<IconWaveSine size={16} />} onClick={() => setBuilding(true)}>
            {t('waves.build')}
          </Button>
        ) : undefined}
      />

      {waves.isLoading ? (
        <Center py="xl"><Loader /></Center>
      ) : waves.isError ? (
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          {waves.error instanceof ApiError ? waves.error.problem.title : t('waves.loadFailed')}
        </Alert>
      ) : waves.data && waves.data.length > 0 ? (
        <ResponsiveTable
          rows={waves.data}
          columns={columns}
          rowKey={(w) => w.pickId}
          minWidth={640}
          actions={(w) => (
            <Button
              size="compact-sm"
              variant="subtle"
              rightSection={<IconChevronRight size={14} />}
              onClick={() => navigate({ to: '/app/pick-waves/$id', params: { id: w.pickId } })}
            >
              {t('waves.open')}
            </Button>
          )}
        />
      ) : (
        <Text c="dimmed">{t('waves.empty')}</Text>
      )}

      <WaveBuilder
        opened={building}
        onClose={() => setBuilding(false)}
        onBuilt={(id) => navigate({ to: '/app/pick-waves/$id', params: { id } })}
      />
    </Stack>
  );
}

interface BuilderProps {
  opened: boolean;
  onClose: () => void;
  onBuilt: (pickId: string) => void;
}

/**
 * Building a wave.
 *
 * Three modes, which are legacy's three procedures: tick the orders yourself, or sweep up every
 * one-unit order, or every multi-unit one, to a limit. The sweeps exist because a picker carrying
 * a tote of singles is a different job from one pushing a pallet truck.
 *
 * The result is shown IN PLACE rather than navigated past, because the verdict on each order is
 * the thing worth reading and it is gone the moment the screen changes.
 */
function WaveBuilder({ opened, onClose, onBuilt }: BuilderProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const warehouseId = useCurrentWarehouseId();
  const [mode, setMode] = useState<WaveMode>('selected');
  const [chosen, setChosen] = useState<string[]>([]);
  const [maxUnits, setMaxUnits] = useState<number | string>(100);
  const [result, setResult] = useState<PickWaveCreatedDto | null>(null);

  const candidates = useQuery({
    queryKey: ['pick-wave-candidates', warehouseId] as const,
    queryFn: ({ signal }) => listWaveCandidates(signal),
    enabled: opened && Boolean(warehouseId) && mode === 'selected',
  });

  const build = useMutation({
    mutationFn: () => createWave(
      mode === 'selected'
        ? { mode, salesOrderIds: chosen }
        : { mode, maxUnits: Number(maxUnits) },
    ),
    onSuccess: async (created) => {
      setResult(created);
      setChosen([]);
      await queryClient.invalidateQueries({ queryKey: ['pick-waves'] });
      await queryClient.invalidateQueries({ queryKey: ['pick-wave-candidates'] });
    },
  });

  const close = () => {
    setResult(null);
    build.reset();
    onClose();
  };

  const columns = useMemo<DataColumn<PickWaveCandidateDto>[]>(() => [
    {
      key: 'pick',
      label: '',
      width: 44,
      render: (c) => (
        <Checkbox
          aria-label={t('waves.chooseOrder', { number: c.orderNumber })}
          checked={chosen.includes(c.salesOrderId)}
          onChange={(e) => setChosen((prev) => (
            e.currentTarget.checked
              ? [...prev, c.salesOrderId]
              : prev.filter((id) => id !== c.salesOrderId)
          ))}
        />
      ),
    },
    {
      key: 'orderNumber',
      label: t('waves.order'),
      render: (c) => <Text fw={600}>{c.orderNumber}</Text>,
      card: 'title',
    },
    {
      key: 'customer',
      label: t('waves.customer'),
      render: (c) => c.companyName ?? c.customerNumber,
    },
    {
      key: 'units',
      label: t('waves.units'),
      align: 'right',
      render: (c) => c.units,
    },
    {
      key: 'canCover',
      label: t('waves.stock'),
      render: (c) => (
        // Advisory only, and it says so: it looks at this order alone, so two orders can each be
        // green here and only one of them fit once the wave has claimed the stock.
        <Badge color={c.canCover ? 'green' : 'orange'} variant="light">
          {c.canCover ? t('waves.covered') : t('waves.short')}
        </Badge>
      ),
      card: 'aside',
    },
  ], [chosen, t]);

  return (
    <Modal opened={opened} onClose={close} title={t('waves.buildTitle')} size="xl">
      <Stack>
        {result ? (
          <WaveResult result={result} onOpen={() => { const id = result.pickID; close(); onBuilt(id); }} />
        ) : (
          <>
            <SegmentedControl
              value={mode}
              onChange={(v) => setMode(v as WaveMode)}
              data={[
                { label: t('waves.modeSelected'), value: 'selected' },
                { label: t('waves.modeSingle'), value: 'single_unit' },
                { label: t('waves.modeMulti'), value: 'multi_unit' },
              ]}
            />

            {mode === 'selected' ? (
              candidates.isLoading ? (
                <Center py="xl"><Loader /></Center>
              ) : candidates.data && candidates.data.length > 0 ? (
                <ResponsiveTable
                  rows={candidates.data}
                  columns={columns}
                  rowKey={(c) => c.salesOrderId}
                  minWidth={640}
                />
              ) : (
                <Text c="dimmed">{t('waves.noCandidates')}</Text>
              )
            ) : (
              <NumberInput
                label={t('waves.maxUnits')}
                description={t('waves.maxUnitsHelp')}
                value={maxUnits}
                onChange={setMaxUnits}
                min={1}
                allowNegative={false}
              />
            )}

            {build.isError ? (
              <Alert icon={<IconAlertCircle size={16} />} color="red">
                {build.error instanceof ApiError ? build.error.problem.title : t('waves.buildFailed')}
              </Alert>
            ) : null}

            <Group justify="flex-end">
              <Button variant="default" onClick={close}>{t('shell.cancel')}</Button>
              <Button
                loading={build.isPending}
                disabled={mode === 'selected' && chosen.length === 0}
                onClick={() => build.mutate()}
              >
                {t('waves.build')}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}

/** What the wave took, and what it left — with the reason for each one it left. */
function WaveResult({ result, onOpen }: { result: PickWaveCreatedDto; onOpen: () => void }) {
  const { t } = useTranslation();
  const admitted = result.orders.filter((o) => o.admitted);
  const skipped = result.orders.filter((o) => !o.admitted);

  return (
    <Stack>
      <Text>{t('waves.builtCount', { count: admitted.length })}</Text>

      {skipped.length > 0 ? (
        <Alert icon={<IconAlertCircle size={16} />} color="yellow" title={t('waves.skippedTitle')}>
          <Stack gap={4}>
            {skipped.map((o) => (
              <Text key={o.salesOrderId} size="sm">
                {o.orderNumber} — {o.reason === 'over_max_units'
                  ? t('waves.reasonOverMax')
                  : t('waves.reasonShort')}
              </Text>
            ))}
          </Stack>
        </Alert>
      ) : null}

      <Group justify="flex-end">
        <Button onClick={onOpen}>{t('waves.open')}</Button>
      </Group>
    </Stack>
  );
}
